import React from 'react';
import Dropdown from '../dropdown/dropdown';
import { components } from 'react-select';
import { Loader } from "semantic-ui-react";
import { UUID } from '../../../../utils/utils';

import Icon from '../icon/icon';

import { classnames } from '../../../../utils/classnames';
import './languageSelector.css';
import { useTranslation } from 'react-i18next';

export const LANGUAGES = {
    fr: 'Français (FR)',
    'fr-fr': 'Français (FR)',
    'fr-ca': 'Français (CA)',
    en: 'English (GB)',
    'en-gb': 'English (GB)',
    'en-ca': 'English (CA)',
    'en-us': 'English (US)',
    es: 'Español (ES)',
    'es-es': 'Español (ES)',
    'ca-es': 'Español (CA)',
    it: 'Italiano (IT)',
    'it-it': 'Italiano (IT)',
    br: 'Breton (FR-BRE)',
    'nl-nl': 'Nederlands (NL)',
    'de-ch': 'Deutsh (CH)',
    'de-de': 'Deutsh (DE)',
    eu: 'Basque (EU)',
    'ru-ru': 'Русский (RU)',
    'pt-pt': 'Português (PT)',
    'el-gr': 'Ελληνικά (EL)',
    'tr-tr': 'Türkçe (TR)'
};

// using css and className to display mobile version of the value
function CustomValue({children, className, mobileValue, forceMobile = false, ...props}) {
    return (
        <components.SingleValue className={classnames('neo-dropdown-single-value', className)} {...props}>
            <span aria-hidden={true} className='neo-language-selected-value'>{forceMobile ? mobileValue : children}</span>
            <span aria-hidden={true} className='neo-language-selected-value-mobile'>{mobileValue}</span>
        </components.SingleValue>
    );
}

const CustomMenu = ({ children, options, className, ...props }) => {
	const hasScrollbar = React.useMemo(() =>
		props?.innerRef?.current?.firstChild?.scrollHeight > props?.innerRef?.current?.firstChild?.clientHeight,
		[props?.innerRef?.current?.firstChild]
	);

    const alignDirection = React.useMemo(() => {
        const elemWidth = props?.innerRef?.current?.clientWidth;
        const maxSize = Math.min(window.screen.width, window.innerWidth);
        const leftEnd = props?.innerRef?.current?.getBoundingClientRect()?.left + window.scrollX;
        return leftEnd + elemWidth > maxSize ? 'align-right': 'align-left';
    }, [props?.innerRef?.current, window])

	return (
		<components.Menu className={classnames('neo-dropdown-menu', (hasScrollbar && 'has-scrollbar'), alignDirection)} align={alignDirection} {...props}>
			{children}
		</components.Menu>
	);
};

export default function LanguageSelector({
    onChange = () => {},
    loading = false,
    disabled = false,
    languages = [],
    className = 'neo-language-selector',
    value = null,
    forceMobile = false,
    label = null,
    description = null,
    displayIcon = true,
    ...rest
}) {
    const {t} = useTranslation();
    const languagesOptions = React.useMemo(() => {
        return languages.map(l => l.toLowerCase()).map(languageCode => Object.keys(LANGUAGES).includes(languageCode) &&
            {
                value: languageCode,
                label: LANGUAGES[languageCode],
            }
        )
    }, [languages]);

    const mobileAbrigedValue = React.useMemo(() => {
        const regexAbriged = /\([A-Z-]{2,}\)/gm;
        if (Object.keys(LANGUAGES).includes(value)) {
            const matchFound = LANGUAGES[value].match(regexAbriged);
            if (matchFound?.length > 0) {
                const codeStringArray = [...matchFound[0]];
                return codeStringArray.filter(char => char !== '(' && char !== ')').join('');
            }
        }
        const keysLang = value?.split('-') || [''];
        return (keysLang.length > 1 ? keysLang[1] : keysLang[0]).toUpperCase();
    }, [value]);

    const labelKey = `${UUID()}_language_selector`;

    return (
        <div className={classnames('neo-language', className)}>
            {!label && !description && (
                <p hidden={true} id={`field_${labelKey}_label`}>{t('widget.language-selector.label')}</p>
            )}
            <Dropdown
                {...rest}
                label={label}
                description={description}
                uniqueKey={labelKey}
                disabled={disabled || loading}
                icon={displayIcon ? loading ? <Loader className="neo-icon-spinner" size="mini" active /> : <Icon name="globe" /> : null}
                className={'neo-dropdown-language'}
                value={value}
                options={languagesOptions}
                onChange={(lang) => onChange(lang)}
                components={{
                    SingleValue: (props) => CustomValue({ ...props, forceMobile, mobileValue: mobileAbrigedValue }),
                    Menu: (props) => CustomMenu({ ...props })
                }}
            />
        </div>
    );
}